import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingAuth } from "../../hooks/use-landing-auth";
import excitement from "../../assets/excitement.webp";
import slantPhone from "../../assets/slanted_phone.png";
import appstore from "../../assets/appstore.svg";
import googleplay from "../../assets/googleplay.svg";
import steam_white from "../../assets/steam-white.svg";
import arHowToPlay from "../../assets/ar_how_to.svg";
import textBackground from "../../assets/text_background.svg";
import { HowToPlayPropaganda } from "../svgs/HowToPlay";
import mobileSlantPhone from "../../assets/mobile_slanted_phone.svg";
import mobileHowToPlay from "../../assets/mobile_how_to_play.svg";
import { TermsModal } from "../Authentication/TermsModal";
import { trackEvent } from "../../services/appsflyer";

type Props = {
  landingAuth: ReturnType<typeof useLandingAuth>;
};

export const HowItWorks = ({ landingAuth }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { authenticated, hasPropaganda, createRoom, navigate } =
    landingAuth;

  const playNowLabel = !authenticated
    ? t("new_how_to.play_now")
    : hasPropaganda
      ? t("herobanner.start")
      : t("herobanner.free_trial");

  const onPlayCta = !authenticated ? () => navigate("/join") : createRoom;

  return (
    <section
      className="relative pb-20 -mt-10 bg-bottom bg-no-repeat bg-cover md:-mt-20"
      style={{ backgroundImage: `url(${excitement})` }}
      id="how-to-play"
    >
      <div className="hidden md:block">
        <div
          className="lg:-mt-96 z-20 relative flex flex-col lg:flex-row gap-4 max-w-[1069px] mx-auto"
          dir="ltr"
        >
          <img
            src={slantPhone}
            alt=""
            className="w-full max-w-[304px] mx-auto lg:mx-0"
          />
          {isArabic ? (
            <img
              src={arHowToPlay}
              alt=""
              className="w-full max-w-[697px] mx-auto lg:mx-0"
            />
          ) : (
            <HowToPlayPropaganda />
          )}
        </div>

        <div
          className="flex justify-start px-4 md:px-8"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <div
            className={`max-w-[565px] space-y-4 ${isArabic ? "mr-auto" : "ml-4"}`}
          >
            <div className="flex flex-row gap-x-2">
              <img className="w-36 cursor-pointer" src={appstore} alt="appstore" onClick={() => { trackEvent("store_badge_clicked", { store: "ios" }); window.open('https://apps.apple.com/us/app/%D8%A8%D8%B1%D9%88%D8%A8%D9%88%D9%82%D8%A7%D9%86%D8%AF%D8%A7-%D9%84%D8%B9%D8%A8%D8%A9-%D8%AC%D9%85%D8%A7%D8%B9%D9%8A%D8%A9-%D8%A7%D9%88%D9%86%D9%84%D8%A7%D9%8A%D9%86/id6737456271?l=ar', '_blank'); }}></img>
              <img className="w-36 cursor-pointer" src={steam_white} alt="steam" onClick={() => { trackEvent("store_badge_clicked", { store: "steam" }); window.open('https://store.steampowered.com/app/4088460/_/', '_blank'); }}></img>
              <img className="w-36 cursor-pointer" src={googleplay} alt="googleplay" onClick={() => { trackEvent("store_badge_clicked", { store: "android" }); window.open('https://play.google.com/store/apps/details?id=com.funny1s.funny1s&hl=ar', '_blank'); }}></img>
            </div>
            <p
              className={`text-center font-bold text-white bg-[#6A1F3B] p-4 ${isArabic && "md:text-5xl text-4xl"
                }`}
            >
              {t("new_how_to.prove")}
            </p>
            <p
              className={`p-4 font-bold text-white ${isArabic && "md:text-5xl text-4xl"
                }`}
            >
              {t("new_how_to.free")}
            </p>
            <button
              type="button"
              style={{ backgroundImage: `url(${textBackground})` }}
              className={`p-4 font-bold text-burgundy bg-no-repeat bg-cover bg-center text-center cursor-pointer howitworks-cta-interactive ${isArabic && "md:text-5xl text-4xl"
                }`}
              onClick={onPlayCta}
            >
              {playNowLabel}
            </button>
          </div>
        </div>

        <div
          className="flex justify-start gap-4 mx-auto mt-4 w-max md:mr-20"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <button
            className={`howitworks-pill-btn-base howitworks-pill-btn-interactive ${isArabic && "text-3xl"}`}
            onClick={() => setShowModal(true)}
          >
            {t("new_how_to.term")}
          </button>
          <button
            className={`howitworks-pill-btn-base howitworks-pill-btn-interactive ${isArabic && "text-3xl"}`}
            onClick={() => navigate("/privacyPolicy")}
          >
            {t("new_how_to.privacy")}
          </button>
        </div>
      </div>
      {/* Mobile View */}
      <div className="md:hidden">
        <div className="relative z-20 flex" dir="ltr">
          <img src={mobileSlantPhone} alt="Mobile Slanted Phone" />
          {isArabic ? (
            <img src={mobileHowToPlay} alt="Mobile How To Play" />
          ) : (
            <HowToPlayPropaganda />
          )}
        </div>
        <div
          className="flex justify-start px-4 md:px-8"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <div
            className={`max-w-[220px] space-y-2 ${isArabic ? "mr-auto" : "ml-4"}`}
          >
            <div className="flex flex-row gap-x-2">
              <img className="w-16 cursor-pointer" src={appstore} alt="appstore" onClick={() => { trackEvent("store_badge_clicked", { store: "ios" }); let finalUrl = window?.dynamicOneLinkUrl || 'https://apps.apple.com/us/app/%D8%A8%D8%B1%D9%88%D8%A8%D9%88%D9%82%D8%A7%D9%86%D8%AF%D8%A7-%D9%84%D8%B9%D8%A8%D8%A9-%D8%AC%D9%85%D8%A7%D8%B9%D9%8A%D8%A9-%D8%A7%D9%88%D9%86%D9%84%D8%A7%D9%8A%D9%86/id6737456271?l=ar'; window.open(finalUrl, '_blank'); }}></img>
              <img className="w-16 cursor-pointer" src={steam_white} alt="steam" onClick={() => { trackEvent("store_badge_clicked", { store: "steam" }); window.open('https://store.steampowered.com/app/4088460/_/', '_blank'); }}></img>
              <img className="w-16 cursor-pointer" src={googleplay} alt="googleplay" onClick={() => { trackEvent("store_badge_clicked", { store: "android" }); let finalUrl = window?.dynamicOneLinkUrl || 'https://play.google.com/store/apps/details?id=com.funny1s.funny1s&hl=ar'; window.open(finalUrl, '_blank'); }}></img>
            </div>
            <p
              className={`text-center font-bold text-white bg-[#6A1F3B] px-2 ${isArabic && "text-xl"
                }`}
            >
              {t("new_how_to.prove")}
            </p>
            <p className={`px-2 font-bold text-white ${isArabic && "text-xl"}`}>
              {t("new_how_to.free")}
            </p>
            <button
              type="button"
              style={{ backgroundImage: `url(${textBackground})` }}
              className={`p-4 font-bold text-burgundy bg-no-repeat bg-cover bg-center text-center cursor-pointer w-full howitworks-cta-interactive ${isArabic && "text-xl"
                }`}
              onClick={onPlayCta}
            >
              {playNowLabel}
            </button>
          </div>
        </div>

        <div
          className="flex justify-start gap-4 mt-10 mr-4 w-max"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <button
            className={`howitworks-pill-btn-base howitworks-pill-btn-interactive ${isArabic ? "text-xl" : "text-sm"
              }`}
            onClick={() => setShowModal(true)}
          >
            {t("new_how_to.term")}
          </button>
          <button
            className={`howitworks-pill-btn-base howitworks-pill-btn-interactive ${isArabic ? "text-xl" : "text-sm"
              }`}
            onClick={() => navigate("/privacyPolicy")}
          >
            {t("new_how_to.privacy")}
          </button>
        </div>
      </div>

      {showModal && (
        <TermsModal params={""} setShowModal={() => setShowModal(false)} />
      )}
    </section>
  );
};
